/**
 * Iter 253z-11 · Performance Marketing · Alerts page.
 *
 * Feed of detected anomalies (spend spikes, CTR drops, CPC spikes).
 * Each item can be resolved (marks resolved_at + resolved_by).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, TrendingDown, TrendingUp, RefreshCw, Loader2, SlidersHorizontal, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const KIND_META = {
  spend_spike: { icon: TrendingUp, tone: "danger", label: "Spend spike" },
  cpc_spike:   { icon: TrendingUp, tone: "warn",   label: "CPC spike" },
  ctr_drop:    { icon: TrendingDown, tone: "warn", label: "CTR drop" },
  budget_burn: { icon: AlertTriangle, tone: "danger", label: "Budget burn" },
};

export default function PerformanceAlerts() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [brands, setBrands] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [defaults, setDefaults] = useState({ spend_spike_ratio: 1.4, ctr_drop_ratio: 0.7, cpc_spike_ratio: 1.5 });
  const [thForm, setThForm] = useState({ brand_id: "", spend_spike_ratio: "", ctr_drop_ratio: "", cpc_spike_ratio: "" });
  const [thSaving, setThSaving] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [a, b, t] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/perf/anomalies`),
        api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/perf/thresholds`),
      ]);
      setRows(a.data || []);
      setBrands(b.data || []);
      setThresholds(t.data?.rows || []);
      setDefaults(t.data?.defaults || defaults);
    } catch (e) { toast.error("Failed to load alerts"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const resolve = async (a) => {
    setBusyId(a.id);
    try {
      await api.post(`/perf/anomalies/${a.id}/resolve`);
      toast.success("Resolved");
      setRows((s) => s.map((r) => r.id === a.id ? { ...r, resolved_at: new Date().toISOString() } : r));
    } catch (e) { toast.error("Failed"); }
    finally { setBusyId(null); }
  };

  const shown = rows.filter((a) => showResolved ? true : !a.resolved_at);
  const openCount = rows.filter((a) => !a.resolved_at).length;

  const saveThreshold = async () => {
    if (!thForm.brand_id) { toast.error("Pick a brand"); return; }
    setThSaving(true);
    try {
      const payload = {
        brand_id: thForm.brand_id,
        spend_spike_ratio: thForm.spend_spike_ratio ? Number(thForm.spend_spike_ratio) : null,
        ctr_drop_ratio: thForm.ctr_drop_ratio ? Number(thForm.ctr_drop_ratio) : null,
        cpc_spike_ratio: thForm.cpc_spike_ratio ? Number(thForm.cpc_spike_ratio) : null,
      };
      await api.post(`/workspaces/${activeWorkspaceId}/perf/thresholds`, payload);
      toast.success("Threshold saved · applied on next sync");
      setThForm({ brand_id: "", spend_spike_ratio: "", ctr_drop_ratio: "", cpc_spike_ratio: "" });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setThSaving(false); }
  };

  const deleteThreshold = async (brand_id) => {
    if (!window.confirm("Reset thresholds for this brand back to defaults?")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/perf/thresholds/${brand_id}`);
      toast.success("Reset");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";

  return (
    <>
      <PageHeader
        eyebrow="Performance Marketing"
        title="Anomaly alerts"
        description="Automated detection of spend spikes, CTR drops, and CPC surges — triggered every sync."
        testid="perf-alerts-header"
        actions={
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} data-testid="perf-alerts-show-resolved" />
              Show resolved
            </label>
            <Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="perf-alerts-refresh"><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        <div className="border border-border p-4 text-xs text-muted-foreground" data-testid="perf-alerts-summary">
          <span className="font-semibold text-foreground text-sm">{openCount}</span> open alert(s) · {rows.length - openCount} resolved · Detection runs during every sync.
        </div>

        {busy ? <div className="text-sm text-muted-foreground">Loading…</div>
          : shown.length === 0 ? (
            <div className="border border-border p-10 text-center" data-testid="perf-alerts-empty">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <div className="font-semibold">Nothing to worry about</div>
              <div className="text-xs text-muted-foreground mt-1">No open anomalies. Everything is within normal bounds vs. the 7-day baseline.</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="perf-alerts-list">
              {shown.map((a) => {
                const meta = KIND_META[a.kind] || KIND_META.spend_spike;
                const Icon = meta.icon;
                const toneCls = meta.tone === "danger" ? "border-rose-400 bg-rose-50/40"
                              : meta.tone === "warn" ? "border-amber-400 bg-amber-50/40"
                              : "border-border";
                return (
                  <div key={a.id} className={`border ${toneCls} p-4 flex items-start gap-3`} data-testid={`perf-alert-${a.id}`}>
                    <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${meta.tone === "danger" ? "text-rose-600" : "text-amber-600"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="chip text-[9px]">{meta.label}</span>
                        <span className="chip text-[9px]">{a.severity}</span>
                        {a.resolved_at && <span className="chip chip-approved text-[9px]">Resolved</span>}
                      </div>
                      <div className="font-semibold text-sm mt-1">{a.entity_name || a.entity_type}</div>
                      <div className="text-xs text-muted-foreground">{a.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">Detected {a.detected_at?.slice(0, 16).replace("T", " ")} · date: {a.date || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/app/performance/campaigns`}>
                        <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px]">Open campaign</Button>
                      </Link>
                      {!a.resolved_at && (
                        <Button size="sm" className="rounded-none h-7 text-[10px]" onClick={() => resolve(a)} disabled={busyId === a.id} data-testid={`perf-alert-resolve-${a.id}`}>
                          {busyId === a.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                          Mark resolved
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }

        {/* Iter 253z-12 · Per-brand anomaly thresholds */}
        <section className="border border-border" data-testid="perf-thresholds-section">
          <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-primary" />
            <div className="font-bold text-sm">Per-brand alert thresholds</div>
            <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
              Defaults: spend {defaults.spend_spike_ratio}× · CTR {defaults.ctr_drop_ratio}× · CPC {defaults.cpc_spike_ratio}×
            </span>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-[11px] text-muted-foreground">
              Alerts are triggered when today&apos;s metrics cross these ratios vs a 7-day baseline. Leave a field blank to use the workspace default. Numbers &gt; 1 for spikes; &lt; 1 for drops.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
              <div>
                <div className="label-eyebrow mb-1 text-[9px]">Brand</div>
                <Select value={thForm.brand_id || "_none"} onValueChange={(v) => setThForm({ ...thForm, brand_id: v === "_none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-xs" data-testid="perf-th-brand"><SelectValue placeholder="Pick brand" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Select brand —</SelectItem>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-eyebrow mb-1 text-[9px]">Spend spike (× avg)</div>
                <Input type="number" step="0.1" min="1" max="10" value={thForm.spend_spike_ratio}
                       onChange={(e) => setThForm({ ...thForm, spend_spike_ratio: e.target.value })}
                       placeholder={String(defaults.spend_spike_ratio)}
                       className="h-9 text-xs tabular-nums" data-testid="perf-th-spend" />
              </div>
              <div>
                <div className="label-eyebrow mb-1 text-[9px]">CTR drop (× avg)</div>
                <Input type="number" step="0.05" min="0.1" max="1" value={thForm.ctr_drop_ratio}
                       onChange={(e) => setThForm({ ...thForm, ctr_drop_ratio: e.target.value })}
                       placeholder={String(defaults.ctr_drop_ratio)}
                       className="h-9 text-xs tabular-nums" data-testid="perf-th-ctr" />
              </div>
              <div>
                <div className="label-eyebrow mb-1 text-[9px]">CPC spike (× avg)</div>
                <Input type="number" step="0.1" min="1" max="10" value={thForm.cpc_spike_ratio}
                       onChange={(e) => setThForm({ ...thForm, cpc_spike_ratio: e.target.value })}
                       placeholder={String(defaults.cpc_spike_ratio)}
                       className="h-9 text-xs tabular-nums" data-testid="perf-th-cpc" />
              </div>
              <Button onClick={saveThreshold} disabled={thSaving || !thForm.brand_id} className="rounded-none h-9" data-testid="perf-th-save">
                {thSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </Button>
            </div>

            {thresholds.length > 0 && (
              <table className="w-full text-xs border border-border" data-testid="perf-th-table">
                <thead className="bg-muted/20 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Brand</th>
                    <th className="text-right px-3 py-2">Spend spike</th>
                    <th className="text-right px-3 py-2">CTR drop</th>
                    <th className="text-right px-3 py-2">CPC spike</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {thresholds.map((t) => (
                    <tr key={t.brand_id} data-testid={`perf-th-row-${t.brand_id}`}>
                      <td className="px-3 py-2 font-medium">{brandName(t.brand_id)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.spend_spike_ratio ?? <span className="text-muted-foreground">(default)</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.ctr_drop_ratio ?? <span className="text-muted-foreground">(default)</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.cpc_spike_ratio ?? <span className="text-muted-foreground">(default)</span>}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 text-rose-600" onClick={() => deleteThreshold(t.brand_id)} data-testid={`perf-th-del-${t.brand_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
